import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';

import { Field, ControlSize } from '../field';
import { Input } from '../input';
import { Tooltip } from '../tooltip';
import { Arrow } from '../icons/arrow';
import { QuestionsCircleIcon } from '../icons/question-circle';

type SelectProps = {
  size: ControlSize;
  label?: string;
  items: string[];
  className?: string;
  placeholder?: string;
  clickToOpenClose?: boolean;
  onChange?: (a: string) => void;
  selected?: string;
  error?: string;
  showError?: boolean;
  required?: boolean;
  helper?: string;
  disabled?: boolean;
  isChange?: boolean;
  readOnly?: boolean;
};

export const CustomSelect: FC<SelectProps> = ({
  size,
  label,
  items,
  className,
  placeholder,
  clickToOpenClose = false,
  onChange,
  selected,
  error = '',
  showError = true,
  required = false,
  helper = '',
  disabled = false,
  isChange = true,
  readOnly = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');

  const handleChange = (val: string) => {
    setSearchVal('');
    onChange && onChange(val);
    setOpen(false);
  };

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (open && ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [open]);

  const shownItems = items.filter((el) => (searchVal ? el.toLowerCase().includes(searchVal.toLowerCase()) : el));

  return (
    <Field
      size={size}
      label={
        label && helper ? (
          // A long explanation stays out of the form: it opens on the «?» instead of taking lines under the field.
          <span className="inline-flex items-center gap-2">
            {label}
            <Tooltip text={helper}>
              <QuestionsCircleIcon width={16} height={16} />
            </Tooltip>
          </span>
        ) : (
          label
        )
      }
      required={required}
      error={showError ? error : undefined}
      invalid={Boolean(error)}
      className={className}
    >
      <div className="relative" ref={ref}>
        <div className={classNames('relative', open && 'z-20')}>
          <Input
            size={size}
            readOnly={readOnly}
            className={classNames(readOnly && 'cursor-pointer select-none')}
            placeholder={placeholder}
            autoComplete="off"
            value={selected || searchVal || ''}
            disabled={disabled}
            onChange={(e) => {
              if (!isChange) return;
              onChange && onChange(e.target.value);
              setSearchVal(e.target.value);
            }}
            onClick={() => {
              if (clickToOpenClose) setOpen((v) => !v);
            }}
            onFocus={() => {
              if (!clickToOpenClose) setOpen(true);
            }}
            icon={
              shownItems.length > 0
                ? {
                    type: 'suffix',
                    icon: (
                      <span onClick={() => !disabled && setOpen(!open)}>
                        <Arrow
                          className={classNames('w-3 cursor-pointer transition-transform', open && 'rotate-180')}
                        />
                      </span>
                    ),
                  }
                : null
            }
          />
        </div>
        {open && shownItems.length > 0 && (
          // `list-none!`: styles/index.css sets `ul { list-style: initial }` outside any layer.
          <ul className="absolute left-0 right-0 top-[calc(100%-10px)] z-10 flex max-h-[164px] list-none! flex-col overflow-y-auto rounded-b-control border border-t-0 border-line-strong bg-surface px-5 pb-2 pt-4">
            {shownItems.map((el, idx) => (
              <li
                key={idx}
                onClick={() => handleChange(el)}
                className={classNames(
                  'cursor-pointer break-words border-b border-line-strong p-3 text-ink last:border-none hover:rounded-chip hover:bg-accent-soft',
                  selected === el && 'rounded-chip bg-accent text-accent-ink',
                )}
              >
                {el}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
};
